import axios from '@/lib/axios'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { useToast } from 'vue-toastification'

import useCustomMutation from '@/composables/useCustomMutation'

import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'
import { Media } from '@/interfaces/media'
import { MutationParams } from '@/interfaces/mutationParams'

interface Props {
	params?: Params
	immediate?: boolean
}

interface Params {
	userId: string | number
	collections: string[]
}

interface CreateParams {
	userId: number
	collection: string
	media: File
	expirationDate?: string | null
}

interface UpdateParams {
	id: number
	userId: number
	expirationDate: string
}

interface DeleteParams {
	id: number
	userId: number
}

const getUserMedia = async (params?: Params) => {
	if (!params) return

	const { data } = await axios.get<Media[]>(
		`api/users/${params.userId}/medias`,
		{
			errorHandling: false,
			params: {
				collections: params.collections,
			},
		} as CustomAxiosRequestConfig
	)
	return data
}

const createUserMedia = async (params: CreateParams) => {
	const { userId, media, collection, expirationDate } = params
	const formData = new FormData()
	formData.append('media', media)
	formData.append('collection', collection)

	if (expirationDate != null) {
		formData.append('expirationDate', expirationDate)
	}

	const { data } = await axios.post(`api/users/${userId}/medias`, formData)
	return data
}

const updateUserMedia = async (params: UpdateParams) => {
	const { id, userId, expirationDate } = params
	const { data } = await axios.put(`api/users/${userId}/medias/${id}`, {
		expirationDate,
	})
	return data
}

const deleteUserMedia = async (params: DeleteParams) => {
	const { id, userId } = params
	const { data } = await axios.delete(`api/users/${userId}/medias/${id}`)
	return data
}

const useUserMedia = (props: Props) => {
	const { params, immediate = false } = props
	const toast = useToast()

	const { data, isLoading, isError, refetch } = useQuery(
		['userMedia', params],
		() => getUserMedia(params),
		{
			enabled: !!params && immediate,
			retry: false,
		}
	)

	const createMutation = useMutation(
		({ params }: MutationParams<CreateParams>) => createUserMedia(params),
		useCustomMutation(toast, 'create')
	)

	const updateMutation = useMutation(
		({ params }: MutationParams<UpdateParams>) => updateUserMedia(params),
		useCustomMutation(toast, 'update')
	)

	const deleteMutation = useMutation(
		({ params }: MutationParams<DeleteParams>) => deleteUserMedia(params),
		useCustomMutation(toast, 'delete')
	)

	return {
		userMedia: data ?? [],
		isLoading,
		isError,

		userMediaCreateMutation: createMutation,
		userMediaCreate: createMutation.mutate,
		userMediaCreateAsync: createMutation.mutateAsync,

		userMediaUpdateMutation: updateMutation,
		userMediaUpdate: updateMutation.mutate,
		userMediaUpdateAsync: updateMutation.mutateAsync,

		userMediaDeleteMutation: deleteMutation,
		userMediaDelete: deleteMutation.mutate,
		userMediaDeleteAsync: deleteMutation.mutateAsync,

		refetch,
	}
}

export default useUserMedia
